import { LabType, OldLabInfo } from "../models/old-labs.model";

export const DEFAULT_EGFR_CONTRAST_MULTIPLIER = 2;

export const EGFR_CONTRAST_MULTIPLIER_OPTIONS = [1, 2, 3, 4, 5] as const;

export type LabResultsEgfrContrastFields = {
  eGFR?: unknown;
  maxContrast?: number | "";
};

export interface EgfrAutoCalcPatient {
  dob?: string;
  age?: number;
  gender?: string;
}

function parseLabNumeric(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed =
    typeof value === "number" ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function snapToEgfrContrastMultiplierOption(value: number): number {
  const rounded = Math.round(value);
  if (
    EGFR_CONTRAST_MULTIPLIER_OPTIONS.includes(
      rounded as (typeof EGFR_CONTRAST_MULTIPLIER_OPTIONS)[number]
    )
  ) {
    return rounded;
  }
  return DEFAULT_EGFR_CONTRAST_MULTIPLIER;
}

/**
 * Derives the display multiplier from persisted eGFR and Max Contrast (not stored in DB).
 * Max Contrast ÷ eGFR, snapped to 1–5.
 */
export function inferEgfrContrastMultiplierFromSaved(
  egfr: unknown,
  maxContrast: unknown
): number {
  const egfrNum = parseLabNumeric(egfr);
  const maxContrastNum = parseLabNumeric(maxContrast);
  if (egfrNum === null || maxContrastNum === null || egfrNum === 0) {
    return DEFAULT_EGFR_CONTRAST_MULTIPLIER;
  }
  return snapToEgfrContrastMultiplierOption(maxContrastNum / egfrNum);
}

/** Max contrast = eGFR × multiplier (multiplier is UI-only). */
export function calculateMaxContrastFromEgfr(
  egfr: unknown,
  multiplier: number = DEFAULT_EGFR_CONTRAST_MULTIPLIER
): number | "" {
  const egfrNum = parseLabNumeric(egfr);
  if (egfrNum === null) {
    return "";
  }
  return egfrNum * snapToEgfrContrastMultiplierOption(multiplier);
}

/** Updates maxContrast from eGFR and UI multiplier; does not persist multiplier. */
export function applyMaxContrastFromEgfrMultiplier(
  labResults: LabResultsEgfrContrastFields | null | undefined,
  multiplier: number
): void {
  if (!labResults) {
    return;
  }
  labResults.maxContrast = calculateMaxContrastFromEgfr(
    labResults.eGFR,
    multiplier
  );
}

/** Max contrast from dedicated eGFR lab, falling back to Full Metabolic legacy storage. */
export function getMaxContrastFromLabs(
  labs: { type?: string; labResults?: LabResultsEgfrContrastFields }[] | null | undefined
): number | "" {
  const list = labs || [];
  const egfrLab = list.find((lab) => String(lab?.type || "").trim() === "eGFR");
  const egfrMax = egfrLab?.labResults?.maxContrast;
  if (egfrMax !== "" && egfrMax != null) {
    return egfrMax;
  }
  const fullMetabolic = list.find(
    (lab) => String(lab?.type || "").trim() === "Full Metabolic"
  );
  const legacyMax = fullMetabolic?.labResults?.maxContrast;
  return legacyMax !== "" && legacyMax != null ? legacyMax : "";
}

function labRecordedTimestamp(lab: OldLabInfo): number {
  const raw = lab?.updatedAt || lab?.verifiedAt || lab?.labDate || lab?.whenDate;
  const parsed = new Date(String(raw ?? "")).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Latest creatinine from Chem 8 or Full Metabolic labs (by recorded date). */
export function getLatestCreatinineFromLabs(
  labs: OldLabInfo[] | null | undefined
): { value: number; sourceLabel: string } | null {
  const sourceTypes = new Set([LabType.Chem8, LabType.FullMetabolic]);
  let latest: { value: number; sourceLabel: string; ts: number } | null = null;

  for (const lab of labs || []) {
    const type = String(lab?.type || "").trim();
    if (!sourceTypes.has(type as LabType)) {
      continue;
    }
    const creatinine = parseLabNumeric(lab.labResults?.creatinine);
    if (creatinine === null) {
      continue;
    }
    const ts = labRecordedTimestamp(lab);
    if (!latest || ts >= latest.ts) {
      latest = {
        value: creatinine,
        sourceLabel: type,
        ts,
      };
    }
  }

  if (!latest) {
    return null;
  }
  return { value: latest.value, sourceLabel: latest.sourceLabel };
}

export function isPatientFemaleForEgfr(
  gender: string | null | undefined
): boolean | null {
  const normalized = String(gender || "").trim().toLowerCase();
  if (normalized === "female" || normalized === "f") {
    return true;
  }
  if (normalized === "male" || normalized === "m") {
    return false;
  }
  return null;
}

/**
 * CKD-EPI creatinine equation (2021, race-free) — same family as DaVita GFR calculator.
 * Creatinine is expected in mg/dL.
 */
export function calculateEgfrCkdEpi2021Creatinine(
  creatinineMgDl: number,
  ageYears: number,
  isFemale: boolean
): number {
  const kappa = isFemale ? 0.7 : 0.9;
  const alpha = isFemale ? -0.241 : -0.302;
  const ratio = creatinineMgDl / kappa;
  const egfr =
    142 *
    Math.pow(Math.min(ratio, 1), alpha) *
    Math.pow(Math.max(ratio, 1), -1.2) *
    Math.pow(0.9938, ageYears);
  return Math.round(egfr);
}

export function resolvePatientAgeYears(
  patient: EgfrAutoCalcPatient | null | undefined,
  calculateAgeFromDob: (dob: string) => number
): number | null {
  if (patient?.age != null && patient.age > 0) {
    return patient.age;
  }
  const dob = String(patient?.dob || "").trim();
  if (!dob) {
    return null;
  }
  const age = calculateAgeFromDob(dob);
  return age > 0 ? age : null;
}

/** Auto eGFR for a new eGFR lab when patient demographics and creatinine are available. */
export function calculateEgfrForNewLabEntry(
  labs: OldLabInfo[] | null | undefined,
  patient: EgfrAutoCalcPatient | null | undefined,
  calculateAgeFromDob: (dob: string) => number
): number | null {
  const creatinineSource = getLatestCreatinineFromLabs(labs);
  if (!creatinineSource) {
    return null;
  }
  const ageYears = resolvePatientAgeYears(patient, calculateAgeFromDob);
  if (ageYears === null) {
    return null;
  }
  const isFemale = isPatientFemaleForEgfr(patient?.gender);
  if (isFemale === null) {
    return null;
  }
  return calculateEgfrCkdEpi2021Creatinine(
    creatinineSource.value,
    ageYears,
    isFemale
  );
}
